import { Link, router } from "@inertiajs/react"
import { Edit, Plus, Trash2 } from "lucide-react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type {
  AppliedFilters,
  AppliedSort,
  DataColumn,
  FilterOption,
} from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"
import type { StatusTone } from "@/lib/status-colors"

type AdminLocationRow = {
  id: number
  name: string
  status: string
  statusLabel: string
  pickupDinerFeePercentage: string
  pickupRestaurantFeePercentage: string
  staffCount: number
  tableCount: number
  createdAt: string
}

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  locations: AdminLocationRow[]
  pagination: Pagination
  search: string
  sort: AppliedSort
  filters: AppliedFilters
  statusOptions: FilterOption[]
  flash?: {
    notice?: string
    alert?: string
  }
}

const STATUS_TONES: Record<string, StatusTone> = {
  active: "available",
  inactive: "disabled",
  suspended: "danger",
}

/**
 * Only the props the table itself owns are reloaded when an operator sorts,
 * filters, searches or pages. The header, the flash and the layout stay put.
 */
const TABLE_PROPS = [ "locations", "pagination", "search", "sort", "filters" ]

export default function AdminLocationsIndex({
  locations,
  pagination,
  search,
  sort,
  filters,
  statusOptions,
  flash = {},
}: Props) {
  const destroyLocation = (locationId: number) => {
    router.delete(`/locations/${locationId}`, { preserveScroll: true })
  }

  const renderActions = (location: AdminLocationRow) => (
    <>
      <Button asChild variant="outline" size="sm">
        <Link href={`/locations/${location.id}/edit`}>
          <Edit className="size-4" />
          {t("ui.react.pages.admin.locations.index.edit")}
        </Link>
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button type="button" variant="destructive" size="sm">
            <Trash2 className="size-4" />
            {t("ui.react.pages.admin.locations.index.delete")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ui.react.pages.admin.locations.index.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.react.pages.admin.locations.index.delete_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.react.pages.admin.locations.form.cancel")}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => destroyLocation(location.id)}>
              {t("ui.react.pages.admin.locations.index.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  const columns: DataColumn<AdminLocationRow>[] = [
    {
      accessorKey: "name",
      header: t("ui.react.pages.admin.locations.index.name"),
      field: { type: "title", truncate: true },
      sortKey: "name",
    },
    {
      accessorKey: "status",
      header: t("ui.react.pages.admin.locations.index.status"),
      field: { type: "status", labelKey: "statusLabel", tones: STATUS_TONES },
      sortKey: "status",
      filter: { kind: "enum", options: statusOptions },
      mobile: "secondary",
    },
    {
      id: "pickupFees",
      header: t("ui.react.pages.admin.locations.index.pickup_fees"),
      cell: ({ row }) => (
        <div className="text-sm">
          {row.original.pickupDinerFeePercentage}% / {row.original.pickupRestaurantFeePercentage}%
        </div>
      ),
    },
    {
      accessorKey: "staffCount",
      header: t("ui.react.pages.admin.locations.index.staff"),
      field: { type: "number" },
      sortKey: "staff_count",
      mobile: "meta",
    },
    {
      accessorKey: "tableCount",
      header: t("ui.react.pages.admin.locations.index.tables"),
      field: { type: "number" },
      sortKey: "table_count",
      mobile: "meta",
    },
    {
      accessorKey: "createdAt",
      header: t("ui.react.pages.admin.locations.index.created"),
      field: { type: "date" },
      sortKey: "created_at",
      mobile: "meta",
    },
    {
      id: "actions",
      header: t("ui.react.pages.admin.locations.index.actions"),
      field: { type: "actions", render: renderActions },
      enableHiding: false,
    },
  ]

  return (
    <AdminLayout>
      <MainContainer size="fullWidth">
        <PageHeader
          title={t("ui.react.pages.admin.locations.index.locations")}
          description={t("ui.react.pages.admin.locations.index.showing", {
            count: locations.length,
            total: pagination.totalCount,
          })}
          actions={(
            <Button asChild>
              <Link href="/locations/new">
                <Plus className="size-4" />
                {t("ui.react.pages.admin.locations.index.new_location")}
              </Link>
            </Button>
          )}
        />

        {flash.notice ? (
          <Alert>
            <AlertDescription>{flash.notice}</AlertDescription>
          </Alert>
        ) : null}

        {flash.alert ? (
          <Alert variant="destructive">
            <AlertDescription>{flash.alert}</AlertDescription>
          </Alert>
        ) : null}

        <DataTable
          mode="server"
          url="/locations"
          columns={columns}
          data={locations}
          surface="card"
          search={search}
          sort={sort}
          filters={filters}
          pagination={pagination}
          reloadOnly={TABLE_PROPS}
          searchPlaceholder={t("ui.react.pages.admin.locations.index.search_locations")}
        />
      </MainContainer>
    </AdminLayout>
  )
}
